import { Check } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import UpgradeButton from "@/components/upgrade-button";
import usePricingModal from "@/lib/hooks/use-pricing-modal";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";

const monthlyPrice = 14.99;
const yearlyPriceA = 99.99;

const UpgradeForm = () => {
  const { studySetId, paywallType } = usePricingModal();
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly");
  const t = useI18n();

  const pricing = {
    yearly: {
      fullPrice: yearlyPriceA,
      monthly: yearlyPriceA / 12,
      weekly: yearlyPriceA / 52,
      savings: ((yearlyPriceA / (monthlyPrice * 12)) * 100).toFixed(0),
    },
    monthly: {
      fullPrice: monthlyPrice,
      monthly: monthlyPrice,
      weekly: monthlyPrice / 4.33,
      savings: 0,
    },
  };

  const planOptions = [
    {
      value: "yearly",
      label: t("upgradeForm.yearly"),
      // @ts-expect-error
      saveText: t("upgradeForm.saveText", {
        amount: pricing.yearly.savings,
      }),
    },
    {
      value: "monthly",
      label: t("upgradeForm.monthly"),
    },
  ];

  const aiFeatures = [
    {
      title: t("upgradeModal.aiFeatures.0"),
    },
    {
      title: t("upgradeModal.aiFeatures.1"),
    },
    {
      title: t("upgradeModal.aiFeatures.2"),
    },
    {
      title: t("upgradeModal.aiFeatures.3"),
    },
    {
      title: t("upgradeModal.aiFeatures.4"),
    },
  ];

  const getPriceDisplay = (planType: "yearly" | "monthly") => {
    const amount = pricing[planType].monthly;
    return `$${amount.toFixed(2)}`;
  };

  return (
    <div className="flex flex-col gap-8">
      <ul className="space-y-1.5">
        {aiFeatures.map((feature) => (
          <li key={feature.title} className="flex items-center gap-1.5">
            <div className="size-[14px] rounded-full grid place-items-center bg-brand-primary">
              <Check className="size-2 text-white" />
            </div>
            <span className="text-sm">{feature.title}</span>
          </li>
        ))}
      </ul>

      <RadioGroup
        value={plan}
        onValueChange={(value: "yearly" | "monthly") => setPlan(value)}
        className="flex flex-col gap-2"
      >
        {planOptions.map((option) => (
          <div
            key={option.value}
            className={cn(
              "flex items-center gap-4 border rounded-lg p-4 cursor-pointer transition-all shadow-custom",
              plan === option.value
                ? "border-brand-primary"
                : "border-gray-200 hover:border-blue-300",
            )}
            onClick={() => setPlan(option.value as "monthly" | "yearly")}
          >
            <div className="flex items-center gap-4 flex-1">
              <div
                className={cn(
                  "relative size-4 rounded-full border-2",
                  plan === option.value
                    ? "border-brand-primary bg-brand-primary"
                    : "border-gray-300",
                )}
              >
                <div
                  className={cn(
                    "absolute inset-0 m-auto size-2 rounded-full bg-white",
                    plan === option.value ? "opacity-100" : "opacity-0",
                  )}
                />
              </div>
              <RadioGroupItem
                value={option.value}
                id={option.value}
                className="sr-only"
              />
              <div className="flex items-center flex-1">
                <Label
                  htmlFor={option.value}
                  className="cursor-pointer text-base"
                >
                  {option.label}
                </Label>
                {option.saveText && (
                  <div className="text-xs ml-2 hidden md:inline-flex items-center justify-center rounded-md px-[6px] py-1 font-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-[#E5F1FF] text-brand-primary">
                    {option.saveText}
                  </div>
                )}
              </div>
            </div>

            <p className="text-base font-medium whitespace-nowrap">
              {getPriceDisplay(option.value as "monthly" | "yearly")}{" "}
              <span className="text-xs font-medium text-muted-foreground">
                {t("upgradeForm.perMonth")}
              </span>
            </p>
          </div>
        ))}
      </RadioGroup>

      <div className="w-full flex flex-col items-center justify-center gap-4">
        <UpgradeButton
          plan={plan === "yearly" ? "yearly" : "monthly"}
          studySetId={studySetId}
          className="w-full h-10 px-4 py-2"
          paywallType={paywallType}
        >
          {t("upgradeForm.upgradeNow")}
        </UpgradeButton>
        <div className="hidden md:flex gap-4 items-center">
          <div className="flex items-center gap-[-6px]">
            <Image
              src={"/upgrade-pfp-1.png"}
              width={20}
              height={20}
              alt="Profile Picture"
            />
            <Image
              src={"/upgrade-pfp-2.png"}
              width={20}
              height={20}
              alt="Profile Picture"
            />
            <Image
              src={"/upgrade-pfp-3.png"}
              width={20}
              height={20}
              alt="Profile Picture"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {/* @ts-ignore */}
            {t("upgradeForm.joinStudents", { count: "11,172" })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeForm;
